import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { Asterisk } from "@/components/Brand";
import NavigationHeader from "@/components/NavigationHeader";
import { LOWDOWN_POSTS, liveLowdownPosts } from "@/data/content";
import { fadeUp, stagger } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MorningArchivePage() {
  const navigate = useNavigate();

  const { data: posts } = useQuery({
    queryKey: ["lowdown"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/lowdown`);
        return data?.length ? liveLowdownPosts(data) : liveLowdownPosts(LOWDOWN_POSTS);
      } catch {
        return liveLowdownPosts(LOWDOWN_POSTS);
      }
    },
    initialData: liveLowdownPosts(LOWDOWN_POSTS),
  });

  const mornings = (posts || []).filter((p) => p.tag === "Morning Glowgetters");

  return (
    <div className="min-h-screen bg-ink text-white">
      <NavigationHeader />

      <main className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8" data-testid="morning-archive-page">
        <motion.div variants={stagger} initial="initial" animate="animate">
          <motion.div variants={fadeUp} className="flex items-center gap-3">
            <Asterisk className="h-5 w-5 text-cherry" />
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cherry">
              Every morning · starts here
            </p>
          </motion.div>
          <motion.h1
            variants={fadeUp}
            className="mt-5 font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl"
          >
            Morning, Glowgetters{" "}
            <span className="font-garamond italic text-cherry">✨ collected.</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="mt-5 max-w-2xl text-base leading-relaxed text-white/60 sm:text-lg">
            The daily word — one clip, one truth, one assignment before the world gets loud.
            Every morning post lives here, newest first. Miss a day, catch up today.
          </motion.p>
          <motion.p
            variants={fadeUp}
            data-testid="morning-archive-count"
            className="mt-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white/40"
          >
            {mornings.length} morning{mornings.length === 1 ? "" : "s"} · and counting
          </motion.p>

          {mornings.length === 0 ? (
            <motion.div variants={fadeUp} className="mt-14 rounded-3xl border border-white/10 bg-panel/60 p-12 text-center">
              <Asterisk className="mx-auto h-7 w-7 text-cherry" />
              <p className="mt-4 font-display text-2xl font-bold">No mornings yet.</p>
              <p className="mt-2 text-sm text-white/50">
                Check back at sunrise, Glowgetters — the daily word drops here first.
              </p>
            </motion.div>
          ) : (
            <motion.div variants={stagger} className="mt-12 grid gap-6 md:grid-cols-2">
              {mornings.map((p, i) => (
                <motion.article
                  key={p.slug}
                  variants={fadeUp}
                  data-testid={`morning-card-${i}`}
                  onClick={() => navigate(`/lowdown/${p.slug}`)}
                  className={`group relative min-h-[240px] cursor-pointer overflow-hidden rounded-3xl border border-white/10 transition-all duration-300 hover:-translate-y-1 hover:border-cherry/50 ${
                    i === 0 ? "md:col-span-2" : ""
                  }`}
                >
                  {p.image_url && (
                    <img
                      src={p.image_url}
                      alt=""
                      className="absolute inset-0 h-full w-full object-cover opacity-35 transition-all duration-500 group-hover:scale-[1.03] group-hover:opacity-60"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/75 to-ink/25" />
                  <div className="relative flex h-full flex-col justify-end p-7 sm:p-9">
                    <div className="flex items-center gap-3">
                      <span className="rounded-full border border-cherry/35 bg-cherry/10 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em] text-cherry">
                        Morning Glowgetters
                      </span>
                      <span className="text-[11px] font-medium text-white/45">{p.date}</span>
                    </div>
                    <h2 className="mt-4 font-display text-xl font-bold leading-tight sm:text-2xl lg:text-3xl">
                      {p.title}
                    </h2>
                    <p className="mt-3 line-clamp-3 max-w-2xl text-sm leading-relaxed text-white/60">
                      {p.excerpt}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-cherry">
                      Read the word
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </motion.article>
              ))}
            </motion.div>
          )}
        </motion.div>
      </main>

      <footer className="border-t border-white/10 px-4 py-10 text-center sm:px-6 lg:px-8">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/35">
          © {new Date().getFullYear()} City Girls Decoded™. Raw. Unfiltered. Necessary.
        </p>
      </footer>
    </div>
  );
}
